/**
 * High-level PDF export API wired to live Dexie data + canvas charts.
 */
import type { SupportedLocale } from "@repo/core";
import { db } from "../db";
import {
  periodLabelFromMonths,
  renderCanvasChartToDataUrl,
  renderDonutChartToDataUrl,
  resolveThemeChartColors,
} from "../exportUtils";
import { createPdfTranslator, monthShort } from "./pdfCopy";
import { buildAnnualReportPayload } from "./buildReportPayload";
import {
  downloadAnnualReportPdf,
  downloadDebtAmortizationPdf,
  downloadMonthlyBudgetPdf,
} from "./renderAnnualReport";
import { computeDebtPayoff } from "../calculations";
import type { Debt } from "../schema";
import { readStoredLocale } from "../../i18n/translations";

/** Canvas size for bar/line charts — 16:9 so PDF embed isn’t stretched flat. */
const CHART_W = 960;
const CHART_H = 540;

export async function exportAnnualReportFromDb(opts: {
  year: number;
  person?: string;
  month?: number;
  currency: string;
  locale: string;
  uiLocale?: SupportedLocale;
  formatCompact?: (n: number) => string;
}): Promise<void> {
  const uiLocale = opts.uiLocale ?? readStoredLocale();
  const t = createPdfTranslator(uiLocale);

  const [transactions, categories, debts, sinkingFunds, netWorthLogs, accounts] =
    await Promise.all([
      db.transactions.toArray(),
      db.categories.toArray(),
      db.debts.toArray(),
      db.sinkingFunds.toArray(),
      db.netWorthLogs.toArray(),
      db.accounts.toArray(),
    ]);

  const payload = buildAnnualReportPayload({
    year: opts.year,
    month: opts.month,
    person: opts.person,
    transactions,
    categories,
    debts,
    sinkingFunds,
    netWorthLogs,
    accounts,
    currency: opts.currency,
    locale: opts.locale,
  });

  const colors = resolveThemeChartColors();
  const cashMonths = payload.monthlyCashFlow.filter((m) => m.income > 0 || m.expense > 0);
  const cashLabels = cashMonths.map((m) => monthShort(m.month, uiLocale, opts.year));
  const cashIncome = cashMonths.map((m) => m.income);
  const cashExpense = cashMonths.map((m) => m.expense);

  const cashFlow =
    cashLabels.length > 0
      ? renderCanvasChartToDataUrl({
          filename: "cashflow",
          title: t("pdf.chart.cashFlow"),
          labels: cashLabels,
          series: [
            { label: t("pdf.chart.income"), color: colors.income, values: cashIncome },
            { label: t("pdf.chart.expense"), color: colors.expense, values: cashExpense },
          ],
          watermark: periodLabelFromMonths(cashLabels, opts.year),
          kind: "bars",
          width: CHART_W,
          height: CHART_H,
          formatTick: opts.formatCompact,
        })
      : null;

  const rule = payload.rule503020;
  const monthLabel = monthShort(payload.month!, uiLocale, opts.year);
  const rule503020 = renderDonutChartToDataUrl({
    title: t("pdf.chart.rule", { month: monthLabel, year: opts.year }),
    slices: [
      { label: t("group.needs"), value: rule.needs, color: colors.primary },
      { label: t("group.wants"), value: rule.wants, color: "hsl(32, 80%, 55%)" },
      { label: t("group.savings"), value: rule.savings, color: colors.assets },
    ],
    watermark: `BudgetOS · ${monthLabel} ${opts.year}`,
    emptyNote: t("pdf.chart.emptySpend"),
    emptyHint: t("pdf.chart.emptyHint"),
  });

  const nw = payload.netWorth.logs;
  const netWorth =
    nw.length > 0
      ? renderCanvasChartToDataUrl({
          filename: "networth",
          title: t("pdf.chart.netWorthTrend"),
          labels: nw.map((l) => l.monthYear),
          series: [
            {
              label: t("pdf.chart.netWorth"),
              color: colors.netWorth,
              values: nw.map((l) => l.net),
              dashed: true,
            },
            {
              label: t("pdf.chart.assets"),
              color: colors.assets,
              values: nw.map((l) => l.assets),
            },
            {
              label: t("pdf.chart.debts"),
              color: colors.destructive,
              values: nw.map((l) => l.debts),
            },
          ],
          watermark: `BudgetOS · ${nw[0]?.monthYear ?? ""}–${nw[nw.length - 1]?.monthYear ?? ""}`,
          kind: "lines",
          width: CHART_W,
          height: CHART_H,
          formatTick: opts.formatCompact,
        })
      : null;

  downloadAnnualReportPdf(payload, { cashFlow, rule503020, netWorth }, undefined, {
    uiLocale,
  });
}

export async function exportMonthlyBudgetFromDb(opts: {
  year: number;
  month: number;
  person?: string;
  currency: string;
  locale: string;
  uiLocale?: SupportedLocale;
}): Promise<void> {
  const uiLocale = opts.uiLocale ?? readStoredLocale();
  const [transactions, categories, debts, sinkingFunds, netWorthLogs, accounts] =
    await Promise.all([
      db.transactions.toArray(),
      db.categories.toArray(),
      db.debts.toArray(),
      db.sinkingFunds.toArray(),
      db.netWorthLogs.toArray(),
      db.accounts.toArray(),
    ]);

  const payload = buildAnnualReportPayload({
    ...opts,
    transactions,
    categories,
    debts,
    sinkingFunds,
    netWorthLogs,
    accounts,
  });
  downloadMonthlyBudgetPdf(payload, undefined, { uiLocale });
}

export function exportDebtAmortization(
  debt: Debt,
  opts: {
    currency: string;
    locale: string;
    uiLocale?: SupportedLocale;
    method?: "snowball" | "avalanche";
    allDebts?: Debt[];
  }
): void {
  const list = opts.allDebts?.length ? opts.allDebts : [debt];
  const results = computeDebtPayoff(list, opts.method ?? "snowball");
  const result = results.find((r) => r.debt.id === debt.id) ?? results[0];
  if (!result) return;
  downloadDebtAmortizationPdf({
    debtName: result.debt.name,
    currency: opts.currency,
    locale: opts.locale,
    schedule: result.schedule,
    totalInterest: result.totalInterest,
    payoffDateIso: result.payoffDate.toISOString(),
    uiLocale: opts.uiLocale ?? readStoredLocale(),
  });
}
